import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import WelcomeHeader from './components/WelcomeHeader';
import QuickActionCard from './components/QuickActionCard';
import RecentActivityList from './components/RecentActivityList';
import ReferenceCodeLookup from './components/ReferenceCodeLookup';
import PendingTasksCard from './components/PendingTasksCard';
import Icon from '../../components/AppIcon';
import { companyService } from '../../services/companyService';
import Button from '../../components/ui/Button';

const ClientDashboardHome = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyIdentifier) {
        setError('Company identifier is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Extract slug from compound identifier (format: slug-uuid)
        // Example: "premium-epoxy-coatings-6d140f4d-dcd8-48ee-98c6-7dc73e84f4e7"
        // Should extract: "premium-epoxy-coatings"
        const extractSlugFromIdentifier = (identifier) => {
          // UUID pattern: 8-4-4-4-12 characters separated by dashes
          // We need to find where the UUID starts and take everything before it
          const parts = identifier?.split('-');
          
          // Find the index where UUID starts (first part that matches UUID segment pattern)
          let uuidStartIndex = -1;
          for (let i = parts?.length - 1; i >= 0; i--) {
            // UUID last segment is 12 chars, check if we have valid UUID pattern
            if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
              // Found potential UUID end, check if previous 4 parts match UUID pattern
              if (i >= 4 && 
                  parts?.[i-1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-1]) &&
                  parts?.[i-2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-2]) &&
                  parts?.[i-3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-3]) &&
                  parts?.[i-4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i-4])) {
                uuidStartIndex = i - 4;
                break;
              }
            }
          }
          
          // If UUID found, return everything before it, otherwise return full identifier
          if (uuidStartIndex > 0) {
            return parts?.slice(0, uuidStartIndex)?.join('-');
          }
          
          return identifier;
        };
        
        const slug = extractSlugFromIdentifier(companyIdentifier);
        const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();
        
        console.log('Dashboard: Original identifier:', companyIdentifier);
        console.log('Dashboard: Extracted slug:', slug);
        console.log('Dashboard: Normalized slug:', normalizedSlug);
        
        // Fetch company data from Supabase using slug
        const companyData = await companyService?.getBySlug(normalizedSlug);
        
        if (!companyData) {
          setError(`Company not found with slug: ${normalizedSlug}`);
          setCompany(null);
        } else {
          console.log('Dashboard: Company loaded successfully:', companyData?.name);
          setCompany(companyData);
        }
      } catch (err) {
        console.error('Error fetching company:', err);
        setError(err?.message || 'Failed to load company data');
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyIdentifier]);

  const recentActivities = [
  {
    id: 1,
    type: 'create',
    title: 'New floor type added',
    description: 'Metallic Pearl finish added to catalog',
    timestamp: new Date(Date.now() - 1800000)?.toISOString()
  },
  {
    id: 2,
    type: 'update',
    title: 'Pricing updated',
    description: 'Flake Epoxy pricing adjusted for Q4 2025',
    timestamp: new Date(Date.now() - 7200000)?.toISOString()
  },
  {
    id: 3,
    type: 'upload',
    title: 'Gallery images uploaded',
    description: '5 new project photos added to showcase',
    timestamp: new Date(Date.now() - 86400000)?.toISOString()
  },
  {
    id: 4,
    type: 'view',
    title: 'Customer design viewed',
    description: 'Reference code ABC123XYZ accessed',
    timestamp: new Date(Date.now() - 172800000)?.toISOString()
  }];


  const pendingTasks = [
  {
    id: 1,
    title: 'Update company profile',
    description: 'Add business hours and contact information',
    priority: 'high',
    icon: 'Building2',
    actionLabel: 'Update Profile'
  },
  {
    id: 2,
    title: 'Add room templates',
    description: 'Upload background images for 3 new room types',
    priority: 'medium',
    icon: 'Home',
    actionLabel: 'Manage Rooms'
  },
  {
    id: 3,
    title: 'Review embed settings',
    description: 'Configure visualizer embed options',
    priority: 'low',
    icon: 'Code',
    actionLabel: 'View Settings'
  }];


  const quickActions = [
  {
    title: 'Manage Floor Types',
    description: 'Add, edit, or remove floor type options and pricing',
    icon: 'Grid3x3',
    actionLabel: 'Manage Types',
    variant: 'primary',
    route: '/floor-types-pricing'
  },
  {
    title: 'Color Swatches',
    description: 'Update available colors and upload swatch images',
    icon: 'Palette',
    actionLabel: 'Manage Colors',
    variant: 'default',
    route: '/colors-management'
  },
  {
    title: 'Room Templates',
    description: 'Configure room options with background images',
    icon: 'Home',
    actionLabel: 'Manage Rooms',
    variant: 'default',
    route: '/rooms-management'
  },
  {
    title: 'Project Gallery',
    description: 'Showcase completed projects and customer designs',
    icon: 'Image',
    actionLabel: 'View Gallery',
    variant: 'success',
    route: '/gallery-projects'
  }];


  const handleQuickAction = (route) => {
    // If we have a company identifier, navigate to company-specific route
    if (companyIdentifier) {
      const routeMap = {
        '/floor-types-pricing': `/client/${companyIdentifier}/floor-types`,
        '/colors-management': `/client/${companyIdentifier}/colors`,
        '/rooms-management': `/client/${companyIdentifier}/rooms`,
        '/gallery-projects': `/client/${companyIdentifier}/gallery`
      };
      navigate(routeMap?.[route] || route);
    } else {
      navigate(route);
    }
  };

  const handleTaskAction = (task) => {
    const routeMap = {
      'Update company profile': companyIdentifier 
        ? `/client/${companyIdentifier}/profile` 
        : '/company-profile-management',
      'Add room templates': companyIdentifier 
        ? `/client/${companyIdentifier}/rooms` 
        : '/rooms-management',
      'Review embed settings': companyIdentifier 
        ? `/client/${companyIdentifier}/embed` 
        : '/embed-links-management'
    };
    const route = routeMap?.[task?.title];
    if (route) navigate(route);
  };

  const handleReferenceLookup = (result) => {
    console.log('Reference code lookup result:', result);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading dashboard...</p>
        </div>
      </div>);

  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            {error ? 'Error Loading Company' : 'Company Not Found'}
          </h2>
          <p className="text-text-secondary mb-6">
            {error || 'The company you\'re looking for doesn\'t exist or has been deactivated.'}
          </p>
          <Button variant="default" onClick={() => navigate('/')}>
            Return Home
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <ClientDashboardTabs />
      <div className="main-content-with-tabs">
        <WelcomeHeader company={company} />

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-text-secondary mb-4">
              <Icon name="Home" size={14} />
              <span>{company?.name}</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary">Dashboard</span>
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Dashboard Overview</h2>
            <p className="text-text-secondary">
              Manage your visualizer configuration and view recent activity
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions?.map((action, index) =>
                <QuickActionCard
                  key={index}
                  title={action?.title}
                  description={action?.description}
                  icon={action?.icon}
                  actionLabel={action?.actionLabel}
                  variant={action?.variant}
                  onAction={() => handleQuickAction(action?.route)} />

                )}
              </div>
            </div>

            <div>
              <ReferenceCodeLookup onLookup={handleReferenceLookup} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="Activity" size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
                </div>
                <RecentActivityList activities={recentActivities} />
              </div>
            </div>

            <div>
              <PendingTasksCard tasks={pendingTasks} onTaskAction={handleTaskAction} />
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Icon name="Sparkles" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Need Help Getting Started?
                </h3>
                <p className="text-sm text-text-secondary mb-4">
                  Check out our comprehensive guide to set up your visualizer and start showcasing your epoxy flooring options to customers.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                    <Icon name="BookOpen" size={14} />
                    View Documentation
                  </button>
                  <button className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                    <Icon name="Video" size={14} />
                    Watch Tutorial
                  </button>
                  <button className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                    <Icon name="MessageCircle" size={14} />
                    Contact Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default ClientDashboardHome;